import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit as qlimit,
} from 'firebase/firestore';
import { functions, db } from '../../lib/firebase.js';

interface CrawlJob {
  phase?: 'listing' | 'ingesting' | 'done' | 'error';
  discovered?: number;
  processed?: number;
  ingested?: number;
  cap?: number;
  currentFile?: string | null;
  currentDest?: string | null;
  bucket?: string;
  errorCount?: number;
  errors?: Array<{ path: string; error: string }>;
}

interface CrawlDoc {
  id: string;
  originalName?: string;
  status?: string;
  category?: string;
}

const PIPELINE_STAGES = ['uploaded', 'extracting', 'classifying', 'embedding', 'ready', 'triage', 'error'] as const;

const STAGE_LABEL: Record<string, string> = {
  uploaded: 'Queued',
  extracting: 'Extracting text',
  classifying: 'Classifying',
  embedding: 'Embedding',
  ready: 'Ready',
  triage: 'Needs review',
  error: 'Error',
};

const STAGE_COLOR: Record<string, string> = {
  uploaded: 'bg-stone-300',
  extracting: 'bg-sky-400',
  classifying: 'bg-amber-400',
  embedding: 'bg-violet-400',
  ready: 'bg-emerald-500',
  triage: 'bg-orange-400',
  error: 'bg-[#9C4A3A]',
};

/**
 * Crawl-into-SFP control + live progress. Starts a dropboxCrawl with a
 * client-minted jobId, then subscribes to:
 *   - crawlJobs/{jobId}  → completion bar + activity (current file → dest)
 *   - documents where crawlJobId == jobId → downstream pipeline status
 */
export function DropboxCrawlPanel({ initialPath }: { initialPath: string }) {
  const [path, setPath] = useState(initialPath);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<CrawlJob | null>(null);
  const [docs, setDocs] = useState<CrawlDoc[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const unsubJob = useRef<(() => void) | null>(null);
  const unsubDocs = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      unsubJob.current?.();
      unsubDocs.current?.();
    };
  }, []);

  function subscribe(id: string) {
    unsubJob.current?.();
    unsubDocs.current?.();
    unsubJob.current = onSnapshot(doc(db, 'crawlJobs', id), (snap) => {
      setJob((snap.data() as CrawlJob) ?? null);
    });
    unsubDocs.current = onSnapshot(
      query(
        collection(db, 'documents'),
        where('crawlJobId', '==', id),
        orderBy('uploadedAt', 'desc'),
        qlimit(200),
      ),
      (snap) => {
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CrawlDoc, 'id'>) })));
      },
      () => {
        /* index may still be building; ignore */
      },
    );
  }

  async function start() {
    if (
      !confirm(
        `Crawl "${path || '(root)'}" and ingest every file into SFP? Each file runs through text extraction, Claude classification, and Voyage embeddings — there's a real $ cost.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    const id = crypto.randomUUID();
    setJobId(id);
    setJob({ phase: 'listing', discovered: 0, processed: 0 });
    setDocs([]);
    subscribe(id);
    try {
      const fn = httpsCallable<unknown, { jobId: string; ingested: number }>(functions, 'dropboxCrawl');
      await fn({ path, maxFiles: 500, jobId: id });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Crawl failed');
    } finally {
      setBusy(false);
    }
  }

  const discovered = job?.discovered ?? 0;
  const processed = job?.processed ?? 0;
  const pct = discovered > 0 ? Math.round((processed / discovered) * 100) : job?.phase === 'done' ? 100 : 0;
  const phase = job?.phase;

  // Downstream pipeline tallies.
  const stageCounts: Record<string, number> = {};
  for (const d of docs) stageCounts[d.status ?? 'uploaded'] = (stageCounts[d.status ?? 'uploaded'] ?? 0) + 1;
  const readyCount = stageCounts['ready'] ?? 0;

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-stone-800">Crawl into SFP</h2>
      <p className="mt-1 text-xs text-stone-500">
        Recursively download every file under the chosen path and run it through the document
        pipeline (text extract → Claude classify → Voyage embed). Capped at 500 files per run.
      </p>

      <div className="mt-3 flex gap-2">
        <input
          value={path}
          onChange={(e) => setPath(e.target.value)}
          placeholder='Start path (e.g. "/SFP/Documents")'
          className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-xs"
        />
        <button
          type="button"
          onClick={() => void start()}
          disabled={busy || phase === 'listing' || phase === 'ingesting'}
          className="rounded bg-[#2F4F3D] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {phase === 'listing' || phase === 'ingesting' ? 'Crawling…' : 'Crawl'}
        </button>
      </div>

      {err && <div className="mt-2 text-xs text-[#9C4A3A]">{err}</div>}

      {jobId && job && (
        <div className="mt-4 space-y-4">
          {/* Phase + destination */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5">
              <span
                className={
                  phase === 'done'
                    ? 'h-2 w-2 rounded-full bg-emerald-500'
                    : 'h-2 w-2 animate-pulse rounded-full bg-amber-400'
                }
              />
              <span className="font-medium text-stone-700">
                {phase === 'listing'
                  ? 'Discovering files…'
                  : phase === 'ingesting'
                  ? 'Downloading + staging…'
                  : phase === 'done'
                  ? 'Crawl complete'
                  : phase}
              </span>
            </span>
            <span className="text-stone-500">
              {processed} / {discovered || '…'} files
            </span>
          </div>

          {/* Completion bar */}
          <div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
              <div
                className="h-full rounded-full bg-[#2F4F3D] transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-stone-400">
              <span>{pct}%</span>
              {job.errorCount ? <span className="text-[#9C4A3A]">{job.errorCount} error(s)</span> : <span />}
            </div>
          </div>

          {/* Activity — current file → destination */}
          {phase === 'ingesting' && (
            <div className="rounded border border-stone-200 bg-stone-50 px-3 py-2 text-[11px]">
              <div className="flex items-center gap-1.5 text-stone-600">
                <span className="inline-block h-1.5 w-1.5 animate-ping rounded-full bg-[#2F4F3D]" />
                <span className="truncate font-mono">{job.currentFile ?? '…'}</span>
              </div>
              {job.currentDest && (
                <div className="mt-1 truncate font-mono text-[10px] text-stone-400">→ {job.currentDest}</div>
              )}
            </div>
          )}

          {/* Where files land */}
          <div className="text-[10px] text-stone-400">
            Staged to <span className="font-mono">gs://{job.bucket}/uploads/dropbox/…</span> — then the
            ingest pipeline processes each automatically.
          </div>

          {/* Downstream pipeline status */}
          {docs.length > 0 && (
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px]">
                <span className="font-medium text-stone-700">Processing status</span>
                <span className="text-stone-500">
                  {readyCount} / {docs.length} ready
                </span>
              </div>
              {/* Segmented stage bar */}
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
                {PIPELINE_STAGES.map((s) =>
                  stageCounts[s] ? (
                    <div
                      key={s}
                      className={STAGE_COLOR[s]}
                      style={{ width: `${(stageCounts[s]! / docs.length) * 100}%` }}
                      title={`${STAGE_LABEL[s]}: ${stageCounts[s]}`}
                    />
                  ) : null,
                )}
              </div>
              {/* Legend */}
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {PIPELINE_STAGES.filter((s) => stageCounts[s]).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 text-[10px] text-stone-600">
                    <span className={`h-2 w-2 rounded-full ${STAGE_COLOR[s]}`} />
                    {STAGE_LABEL[s]} {stageCounts[s]}
                  </span>
                ))}
              </div>

              {/* Recent files list */}
              <div className="mt-3 max-h-56 overflow-y-auto rounded border border-stone-200">
                <table className="w-full text-[11px]">
                  <tbody>
                    {docs.slice(0, 60).map((d) => (
                      <tr key={d.id} className="border-b border-stone-100 last:border-b-0">
                        <td className="px-3 py-1.5">
                          <span className="truncate text-stone-700">{d.originalName ?? d.id}</span>
                        </td>
                        <td className="px-2 py-1.5 text-stone-400">{d.category ?? '—'}</td>
                        <td className="px-3 py-1.5 text-right">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${
                              d.status === 'ready'
                                ? 'bg-emerald-100 text-emerald-700'
                                : d.status === 'error'
                                ? 'bg-[#9C4A3A]/10 text-[#9C4A3A]'
                                : d.status === 'triage'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-stone-100 text-stone-600'
                            }`}
                          >
                            {STAGE_LABEL[d.status ?? 'uploaded'] ?? d.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
              Crawl finished — {job.ingested ?? processed} file(s) staged.
              {readyCount < docs.length && docs.length > 0
                ? ' The pipeline is still processing the rest; this updates live.'
                : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
